import { Link, useNavigate } from 'react-router-dom';
import { Mentor } from '../../../mocks/mentors';
import Avatar from '@/components/base/Avatar';

interface Props {
  mentor: Mentor;
  highlight?: string;
}

const highlightText = (text: string, keyword: string) => {
  if (!keyword.trim()) return <span>{text}</span>;
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === keyword.toLowerCase() ? (
          <mark key={i} className="bg-violet-100 text-violet-700 rounded px-0.5 not-italic font-semibold">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

const MentorCard = ({ mentor, highlight = '' }: Props) => {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`/mentor/${mentor.id}`)}
      className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-violet-200 hover:-translate-y-1 transition-all duration-300 group cursor-pointer flex flex-col h-full"
    >
      <div className="relative h-52 overflow-hidden flex-shrink-0">
        <Avatar
          src={mentor.avatar}
          alt={mentor.name}
          className="w-full h-full"
          imgClassName="group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div
          className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-bold backdrop-blur-sm"
          style={{
            backgroundColor: mentor.companyColor + '30',
            color: '#fff',
            border: `1px solid ${mentor.companyColor}60`,
          }}
        >
          {highlightText(mentor.company, highlight)}
        </div>
        <div className="absolute bottom-3 left-3 flex items-center gap-1.5">
          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          <span className="text-white/90 text-xs font-medium">接受預約</span>
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-[#0A1628] text-base truncate">
              {highlightText(mentor.name, highlight)}
            </h3>
            <p className="text-slate-500 text-sm mt-0.5 truncate">
              {highlightText(mentor.title, highlight)}
            </p>
          </div>
          <div className="flex-shrink-0 ml-2 text-right">
            <div className="flex items-center gap-1 justify-end">
              <i className="ri-star-fill text-amber-400 text-xs" />
              <span className="text-sm font-bold text-[#0A1628]">{mentor.rating}</span>
            </div>
            <div className="text-slate-400 text-xs">{mentor.reviews} 評價</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {mentor.skills.slice(0, 3).map((skill) => (
            <span
              key={skill}
              className={`px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                highlight && skill.toLowerCase().includes(highlight.toLowerCase())
                  ? 'bg-violet-200 text-violet-800 ring-1 ring-violet-400'
                  : 'bg-violet-50 text-violet-700'
              }`}
            >
              {highlightText(skill, highlight)}
            </span>
          ))}
          {mentor.skills.length > 3 && (
            <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs font-medium rounded-full">
              +{mentor.skills.length - 3}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 text-xs text-slate-400 mb-4">
          <span className="flex items-center gap-1">
            <i className="ri-user-line" />
            {mentor.sessions}+ 次諮詢
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-translate-2" />
            {mentor.languages.slice(0, 2).join(' · ')}
          </span>
        </div>

        <div className="mt-auto pt-3 border-t border-slate-100">
          {/* Default state: price + view profile */}
          <div className="flex items-center justify-between group-hover:hidden">
            <div>
              <span className="text-xs text-slate-400">起價</span>
              <div className="text-[#0A1628] font-bold text-lg leading-tight">
                ${mentor.price}
                <span className="text-slate-400 text-xs font-normal">/次</span>
              </div>
            </div>
            <Link
              to={`/mentor/${mentor.id}`}
              onClick={(e) => e.stopPropagation()}
              className="px-4 py-2 bg-violet-600 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:bg-violet-700 active:scale-95 transition-all"
            >
              查看主頁
            </Link>
          </div>

          {/* Hover state: two action buttons */}
          <div className="hidden group-hover:flex items-center gap-2">
            <Link
              to={`/mentor/${mentor.id}`}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 text-center py-2 border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:border-violet-300 hover:text-violet-700 transition-all"
            >
              查看主頁
            </Link>
            <Link
              to={`/mentor/${mentor.id}#packages-section`}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 text-center py-2 bg-violet-600 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:bg-violet-700 active:scale-95 transition-all flex items-center justify-center gap-1"
            >
              <i className="ri-calendar-check-line text-xs" />
              預約諮詢
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentorCard;
